import { z } from "zod";
import {
    FinancialAccountType,
    FinancialAccountSourceType,
} from "@common/types/financialAccount";
import { EntityBase } from "@common/types/entity";

export const financialAccountSchema = z.object({
    id: z.number(),
    name: z.string().nullable(),
    isBusiness: z.boolean().nullable(),
    balance: z.number().nullable(),
    balanceSyncedAt: z.coerce.date().nullable(),
    transactionsSyncedAt: z.coerce.date().nullable(),
    continuousReconciliationEnabledAt: z.coerce.date().nullable(),
    createdAt: z.coerce.date().nullable(),
    entityId: z.number().nullable(),
    sourceType: z.nativeEnum(FinancialAccountSourceType),
    routingNumber: z.string().nullable(),
    wireRoutingNumber: z.string().nullable(),
    accountNumberMask: z.string().nullable(),
    disabled: z.boolean(),
    financialType: z.nativeEnum(FinancialAccountType),
    institutionName: z.string().nullable(),
    bookkeepingStartDate: z.string().date().nullable(),
    bookkeepingEndDate: z.string().date().nullable(),
    sortOrder: z.number().optional(),
});

export type FinancialAccountSchema = z.infer<typeof financialAccountSchema>;

export const integrationAccountResponseSchema = z.object({
    id: z.string(),
    connectionId: z.number().nullable(),
    integrationConnectionId: z.string().nullable(),
    sourceType: z.nativeEnum(FinancialAccountSourceType),
    sourceAccountId: z.string().nullable(),
    balanceSyncedAt: z.coerce.date().nullable(),
    transactionsSyncedAt: z.coerce.date().nullable(),
    invoicesSyncedAt: z.coerce.date().nullable(),
    statementsSyncedAt: z.coerce.date().nullable(),
    lastStatementDate: z.string().nullable(),
    createdAt: z.coerce.date().nullable(),
    financialAccountId: z.number().nullable(),
    // z.custom keeps the key required; z.unknown() would infer it optional.
    metadata: z.custom<
        Record<string, unknown> | unknown[] | string | number | boolean | null
    >(() => true),
});

/**
 * Serializes `FinancialAccountWithIntegrationAccounts` at response
 * boundaries. Unknown keys (e.g. integration account `sourceData`) are
 * stripped by Zod; the nested entity passes through as-is.
 *
 * Relations are optionally typed on TypeORM entities, so the input side
 * accepts `undefined`, but validation rejects it: a query that stops
 * loading a relation fails loudly instead of narrowing the response.
 */
export const financialAccountWithIntegrationAccountsResponseSchema =
    financialAccountSchema.extend({
        order: z.number(),
        entity: z
            .custom<EntityBase | null | undefined>(
                (value) => value === null || typeof value === "object",
            )
            .transform((entity) => entity ?? null),
        integrationAccounts: z
            .array(integrationAccountResponseSchema)
            .optional()
            .refine(
                (integrationAccounts) => integrationAccounts !== undefined,
                { message: "integrationAccounts relation is not loaded" },
            )
            .transform((integrationAccounts) => integrationAccounts ?? []),
    });
